import React from "react";
import { CircleCheckIcon } from "../Icons";
import { READING, SectionLabel } from "../ui";

// Formatted server-side by BusinessCase::WorkedExample, from the same calculator the builder uses.
export type WorkedExample = {
  participants: number;
  salary: string;
  turnoverRate: number;
  turnoverCostPct: number;
  reductionPct: number;
  hourlyRate: string;
  departures: string;
  replacementCost: string;
  retentionValue: string;
  adminHours: string;
  adminValue: string;
  totalValue: string;
};

const GUIDE_HEADING = "tw-mt-4 tw-mb-0 tw-font-black tw-tracking-[-.045em] xs:tw-text-[2rem]";

export function CalculationExplainer() {
  return (
    <section id="how-it-works" className="tw-scroll-mt-24 tw-border-0 tw-border-t tw-border-solid tw-border-[#d7e4ef] tw-bg-white tw-py-24 sm:tw-py-16">
      <div className={READING}>
        <SectionLabel>Transparent assumptions</SectionLabel>
        <h2 className={`${GUIDE_HEADING} tw-text-5xl`}>How the calculation works</h2>

        <div className="tw-mt-7 tw-space-y-5 tw-text-lg tw-leading-8 tw-text-[#526a82]">
          <p className="tw-m-0">
            The calculator starts with the outcome you choose. For retention, it multiplies the number of
            participants by average annual salary, current turnover rate, replacement cost and the
            expected reduction in turnover. That gives an estimated retention value, built entirely on
            the figures you enter.
          </p>
          <p className="tw-m-0">
            It then estimates programme admin time from a planning assumption and multiplies the hours
            saved by your hourly rate. That figure is shown on its own line, so your finance team can
            decide whether recovered time counts as useful capacity, avoided cost or neither. The two are
            added together to show potential gross Year 1 value.
          </p>
          <p className="tw-m-0">
            Every organisation-specific input is yours. The scenario choices keep some assumptions inside
            a research-informed range, but they don&rsquo;t predict what your programme will achieve.
            Results are estimates, not promised outcomes or forecast cash savings, and the indicative
            quote is kept separate so you can weigh likely value against likely cost.
          </p>
        </div>
      </div>
    </section>
  );
}

export function WorkedExampleSection({ example }: { example: WorkedExample }) {
  const steps = [
    {
      title: "Expected departures",
      working: `${example.participants} participants × ${example.turnoverRate}% turnover = ${example.departures} expected departures.`,
    },
    {
      title: "Replacement cost",
      working: `${example.salary} salary × ${example.turnoverCostPct}% = ${example.replacementCost} per expected departure.`,
    },
    {
      title: "Retention value",
      working: `${example.departures} × ${example.replacementCost} × ${example.reductionPct}% relative reduction = ${example.retentionValue} estimated value.`,
    },
    {
      title: "Admin time value",
      working: `${example.adminHours} estimated hours at ${example.hourlyRate} an hour = ${example.adminValue}, shown separately.`,
    },
  ];

  return (
    <section className="tw-border-y tw-border-[#d7e4ef] tw-bg-[#f4f8fc] tw-py-20 sm:tw-py-16">
      <div className={READING}>
        <SectionLabel>Illustrative example, not a forecast</SectionLabel>
        <h2 className={`${GUIDE_HEADING} tw-text-4xl`}>Worked example</h2>
        <p className="tw-mt-6 tw-mb-0 tw-text-lg tw-leading-8 tw-text-[#526a82]">
          Take a cohort of {example.participants} participants on an average salary of {example.salary},
          with {example.turnoverRate}% annual turnover, replacement costs at {example.turnoverCostPct}% of
          salary and a {example.reductionPct}% relative reduction in turnover.
        </p>

        {/* Numbered because each line uses the one before it. */}
        <ol className="tw-mt-8 tw-mb-0 tw-grid tw-list-none tw-grid-cols-2 tw-gap-4 tw-p-0 sm:tw-grid-cols-1">
          {steps.map((step, index) => (
            <li key={step.title} className="tw-rounded-2xl tw-border tw-border-[#cedfeb] tw-bg-white tw-p-6">
              <strong className="tw-text-[#173754]">
                {index + 1}. {step.title}
              </strong>
              <p className="tw-mt-2 tw-mb-0 tw-leading-7 tw-tabular-nums tw-text-[#60768c]">{step.working}</p>
            </li>
          ))}
        </ol>

        <p className="tw-mt-6 tw-mb-0 tw-rounded-2xl tw-bg-[#10233f] tw-p-6 tw-font-bold tw-leading-7 tw-text-white">
          Potential gross Year 1 value: <span className="tw-tabular-nums tw-text-[#70d9ce]">{example.totalValue}</span>{" "}
          before programme costs. Use it to test your assumptions, not as a forecast.
        </p>
      </div>
    </section>
  );
}

const USAGE = [
  {
    title: "Show every assumption",
    body: "Put the participant count, salaries, workforce rates and expected change next to the result. Leadership can question one input without dismissing the whole case.",
  },
  {
    title: "Use scenarios, not one answer",
    body: "Compare the baseline, planning and upper cases. A range makes the uncertainty visible and shows finance where the decision still holds.",
  },
  {
    title: "Separate value from cash savings",
    body: "Say which benefits are avoided costs, productive capacity or qualitative evidence. An hour recovered isn't cash in the bank.",
  },
  {
    title: "Agree measurement before launch",
    body: "Choose the baseline, owners and review points before the cohort starts. The business case then doubles as your measurement plan.",
  },
] as const;

export function UsingYourCase() {
  return (
    <section className="tw-bg-white tw-py-24 sm:tw-py-16">
      <div className={READING}>
        <SectionLabel>From estimate to decision</SectionLabel>
        <h2 className={`${GUIDE_HEADING} tw-text-4xl`}>How to use your business case</h2>

        <div className="tw-mt-9 tw-grid tw-grid-cols-2 tw-gap-5 sm:tw-grid-cols-1">
          {USAGE.map((item) => (
            <article key={item.title} className="tw-rounded-[22px] tw-border tw-border-[#d4e2ee] tw-p-6">
              <CircleCheckIcon aria-hidden className="tw-h-6 tw-w-6 tw-text-[#0b8c72]" />
              <h3 className="tw-mt-4 tw-mb-0 tw-text-xl tw-font-black tw-text-[#173754]">{item.title}</h3>
              <p className="tw-mt-3 tw-mb-0 tw-leading-7 tw-text-[#60768c]">{item.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
